/**
 * What the palette offers, in the order it offers it.
 *
 * The order is the order a cold letter is actually written in: say who you are
 * and what you found, show the finding, ask for the one action, sign it, and
 * give a way out. A palette sorted alphabetically would put "button" first and
 * "footer" fourth, which teaches the wrong shape to whoever is writing.
 *
 * `hint` is not decoration either. Two of these blocks behave differently in a
 * mail client than the name suggests - columns never stack on a phone, and a
 * raw HTML block is not checked - and the palette is where somebody finds that
 * out, not the inbox.
 */
import {
  AlignLeft,
  Columns2,
  Code2,
  Heading,
  Image,
  Link2,
  List,
  Minus,
  MousePointerClick,
  Quote,
  ShieldCheck,
  Space,
  UserRound,
  type LucideIcon,
} from "lucide-react";
import { COLUMN_CHILD_TYPES, type BlockType } from "./types";

export interface BlockMeta {
  type: BlockType;
  icon: LucideIcon;
  /** i18n suffix: `marketing.builder.block.<key>` and `...blockHint.<key>`. */
  key: string;
  /** True when a mail client does something with this block that its name does
   *  not lead you to expect. The palette marks these. */
  caveat?: boolean;
}

export const BLOCK_CATALOG: BlockMeta[] = [
  { type: "heading", icon: Heading, key: "heading" },
  { type: "text", icon: AlignLeft, key: "text" },
  { type: "list", icon: List, key: "list" },
  { type: "button", icon: MousePointerClick, key: "button" },
  { type: "image", icon: Image, key: "image" },
  { type: "quote", icon: Quote, key: "quote" },
  { type: "columns", icon: Columns2, key: "columns", caveat: true },
  { type: "divider", icon: Minus, key: "divider" },
  { type: "spacer", icon: Space, key: "spacer" },
  { type: "signature", icon: UserRound, key: "signature" },
  { type: "social", icon: Link2, key: "social" },
  { type: "footer", icon: ShieldCheck, key: "footer" },
  { type: "html", icon: Code2, key: "html", caveat: true },
];

const BY_TYPE = new Map(BLOCK_CATALOG.map((m) => [m.type, m]));

export function blockMeta(type: BlockType): BlockMeta {
  return BY_TYPE.get(type) ?? BLOCK_CATALOG[0]!;
}

/** The subset a column will accept. A column holding columns is the one nesting
 *  that reliably breaks in Outlook, so it is not offered rather than offered
 *  and then rejected. */
export function catalogFor(target: "page" | "column"): BlockMeta[] {
  if (target === "page") return BLOCK_CATALOG;
  const allowed = new Set<string>(COLUMN_CHILD_TYPES);
  return BLOCK_CATALOG.filter((m) => allowed.has(m.type));
}

/** The MIME-ish keys the drag carries. `block-type` is a new block from the
 *  palette, `block-id` is one already in the letter being moved. Two keys
 *  rather than one payload so a drop target can tell them apart from
 *  `dataTransfer.types` alone, which is all the browser exposes during dragover. */
export const DRAG_NEW = "application/x-hb-block-type";
export const DRAG_MOVE = "application/x-hb-block-id";
